'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Pause from '@public/svgs/pause.svg'
import LeftArrow from '@public/svgs/left_arrow.svg'
import RightArrow from '@public/svgs/right_arrow.svg'

import { RELATED_SITES } from '@/constants/related-site';
import type { RelatedSite } from '@/types';

/**
 * 관련 사이트 컴포넌트
 * 
 * 홈페이지 하단에 표시되는 관련 사이트들의 로고를 자동 스크롤로 보여주는 컴포넌트입니다.
 * 자동 스크롤과 수동 조작을 모두 지원하며, 무한 루프 스크롤을 제공합니다.
 * 
 * 주요 기능:
 * - 관련 사이트 로고 자동 스크롤 (5초 간격)
 * - 좌우 수동 스크롤 버튼
 * - 재생/정지 토글 버튼
 * - 스크롤 위치에 따른 버튼 활성화/비활성화
 * - 사용자 수동 조작 시 자동 스크롤 일시 정지
 * - 무한 루프 스크롤 (끝에 도달하면 처음으로)
 * - 이미지 로딩 에러 시 텍스트 대체
 */
export function RelatedSites() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isPausedByUser, setIsPausedByUser] = useState(false);

  const sites: RelatedSite[] = RELATED_SITES;

  /**
   * 스크롤 위치를 확인하여 좌우 스크롤 버튼의 활성화 상태를 업데이트하는 함수
   */
  const checkScrollPosition = useCallback(() => {
    if (!scrollRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  /**
   * 자동 스크롤 함수
   * 
   * 5초마다 실행되며, 끝에 도달하면 처음으로 돌아가는 무한 루프를 제공합니다.
   */
  const autoScroll = useCallback(() => {
    if (!scrollRef.current || !isPlaying) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;

    if (scrollLeft >= scrollWidth - clientWidth) {
      // 끝에 도달하면 처음으로 부드럽게 이동
      scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      // 300px씩 오른쪽으로 스크롤
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  }, [isPlaying]);

  /**
   * 왼쪽으로 스크롤하는 함수
   * 
   * 사용자가 수동으로 조작할 때 자동 스크롤을 5초간 일시 정지합니다.
   */
  const scrollLeft = useCallback(() => {
    if (!scrollRef.current) return;

    setIsPausedByUser(true);
    scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });

    // 5초 후 자동 스크롤 재개
    setTimeout(() => setIsPausedByUser(false), 5000);
  }, []);

  /**
   * 오른쪽으로 스크롤하는 함수
   * 
   * 사용자가 수동으로 조작할 때 자동 스크롤을 5초간 일시 정지합니다.
   */
  const scrollRight = useCallback(() => {
    if (!scrollRef.current) return;

    setIsPausedByUser(true);
    scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });

    // 5초 후 자동 스크롤 재개
    setTimeout(() => setIsPausedByUser(false), 5000);
  }, []);

  /**
   * 재생/정지 토글 함수
   * 
   * 자동 스크롤의 재생 상태를 전환합니다.
   */
  const togglePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  // 자동 스크롤 인터벌 설정
  useEffect(() => {
    if (!isPlaying || isPausedByUser) return;

    const interval = setInterval(autoScroll, 5000);
    return () => clearInterval(interval);
  }, [isPlaying, autoScroll, isPausedByUser]);

  // 스크롤 이벤트 리스너 설정
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    checkScrollPosition();
    scrollElement.addEventListener('scroll', checkScrollPosition);

    return () => {
      scrollElement.removeEventListener('scroll', checkScrollPosition);
    };
  }, [checkScrollPosition]);

  // 초기 스크롤 위치 확인 (100ms 지연)
  useEffect(() => {
    const timer = setTimeout(checkScrollPosition, 100);
    return () => clearTimeout(timer);
  }, [checkScrollPosition]);

  return (
    <section className="w-full h-26 flex items-center border-t border-border">
      <div className="w-full max-w-[1540px] mx-auto px-5">
        <div className="flex items-center space-x-6 relative">
          {/* 스크롤 가능한 로고 영역 */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-hidden"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {/* 로고 컨테이너 */}
            <div
              className="flex space-x-20"
              style={{
                width: 'max-content'
              }}
            >
              {/* 관련 사이트 로고들 */}
              {sites.map((site, index) => (
                <a
                  key={index}
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 group"
                >
                  <div className="w-50 h-20 flex items-center justify-center">
                    <div className="w-50 h-[50px] relative">
                      <Image
                        src={site.logo}
                        alt={`${site.name} 로고`}
                        fill
                        className="object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = `<span class="text-sm font-medium text-gray-600 text-center">${site.name}</span>`;
                          }
                        }}
                      />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* 오른쪽 그라데이션 오버레이 */}
          <div className="absolute right-26 top-0 bottom-0 w-22 bg-gradient-to-l from-white to-transparent pointer-events-none z-10" />

          {/* 컨트롤 버튼들 */}
          <div className="flex items-center space-x-1 flex-shrink-0">
            {/* 왼쪽 스크롤 버튼 */}
            <button
              onClick={scrollLeft}
              disabled={!canScrollLeft}
              className={`w-8 h-8 border rounded-full flex items-center justify-center transition-colors cursor-pointer duration-100 ${canScrollLeft
                ? 'bg-white border-border'
                : 'bg-gray-100 border-border text-gray-400 cursor-default'
                }`}
              aria-label="왼쪽으로 스크롤"
            >
              <LeftArrow className="w-[7px] h-[14px]" />
            </button>

            {/* 재생/정지 토글 버튼 */}
            <button
              onClick={togglePlayPause}
              className="w-8 h-8 flex items-center text-foreground justify-center transition-colors cursor-pointer duration-100"
              aria-label={isPlaying ? '정지' : '재생'}
            >
              {isPlaying ? (
                <Pause className="w-[9px] h-[14px]" />
              ) : (
                <svg className="w-[14px] h-[14px]" fill="currentColor" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
                </svg>
              )}
            </button>

            {/* 오른쪽 스크롤 버튼 */}
            <button
              onClick={scrollRight}
              disabled={!canScrollRight}
              className={`w-8 h-8 border rounded-full flex items-center justify-center transition-colors cursor-pointer duration-100 ${canScrollRight
                ? 'bg-white border-border'
                : 'bg-gray-100 border-border text-gray-400 cursor-default'
                }`}
              aria-label="오른쪽으로 스크롤"
            >
              <RightArrow className="w-[7px] h-[14px]" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
} 